/**
 * Utility functions for PlanDesigner
 */

import { CoverageTypeEnum } from "@platform/types";

// Coverage labels that are shared between `coverageTypeToLabel` (human-readable
// mapping) and the backend coverage-name tables below. Extracted as constants
// to satisfy `sonarjs/no-duplicate-string`.
const DENTAL_PPO = "Dental PPO";
const DENTAL_HMO = "Dental HMO";
const CRITICAL_ILLNESS = "Critical Illness";
const HOSPITAL_INDEMNITY = "Hospital Indemnity";
const INDIVIDUAL_DISABILITY_INSURANCE = "Individual Disability Insurance";
const WHOLE_LIFE = "Whole Life";

/**
 * Converts a coverage type enum to a human-readable label.
 *
 * @param type - The coverage type enum value
 * @returns A formatted, human-readable label for the coverage type
 *
 * @example
 * ```ts
 * coverageTypeToLabel(CoverageTypeEnum.DENTAL) // "Dental"
 * coverageTypeToLabel(CoverageTypeEnum.STD) // "Short Term Disability"
 * coverageTypeToLabel(CoverageTypeEnum.BASIC_LIFE_ADND) // "Basic Life AD&D"
 * ```
 */
export const coverageTypeToLabel = (type: CoverageTypeEnum): string => {
  const labelMap: Record<CoverageTypeEnum, string> = {
    [CoverageTypeEnum.DENTAL]: "Dental",
    [CoverageTypeEnum.DENTAL_PPO]: DENTAL_PPO,
    [CoverageTypeEnum.DENTAL_HMO]: DENTAL_HMO,
    [CoverageTypeEnum.VISION]: "Vision",
    [CoverageTypeEnum.STD]: "Short Term Disability",
    [CoverageTypeEnum.LTD]: "Long Term Disability",
    [CoverageTypeEnum.BASIC_LIFE_ADND]: "Basic Life AD&D",
    [CoverageTypeEnum.VOLUNTARY_LIFE_ADND]: "Voluntary Life AD&D",
    [CoverageTypeEnum.ACCIDENT]: "Accident",
    [CoverageTypeEnum.CRITICAL_ILLNESS]: CRITICAL_ILLNESS,
    [CoverageTypeEnum.HOSPITAL_INDEMNITY]: HOSPITAL_INDEMNITY,
    [CoverageTypeEnum.CANCER]: "Cancer",
    [CoverageTypeEnum.FMLA]: "FMLA",
    [CoverageTypeEnum.EAP]: "EAP",
    [CoverageTypeEnum.INDIVIDUAL_DISABILITY_INSURANCE]:
      INDIVIDUAL_DISABILITY_INSURANCE,
    [CoverageTypeEnum.WHOLE_LIFE]: WHOLE_LIFE,
  };
  return labelMap[type] || type;
};

const LAST_SAVED = "last saved";

const formatTimeAgo = (value: number, unit: string): string =>
  `${LAST_SAVED} ${value} ${unit}${value !== 1 ? "s" : ""} ago`;

/**
 * Returns a human-readable "time ago" string for a given date.
 * @param date Date or string or number
 * @returns string like "5 minutes ago", "saved just now", "saved 2 days ago"
 */
export const timeAgo = (date?: Date | string | number): string => {
  const now = new Date();
  const then = date ? new Date(date) : now;
  const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);

  if (isNaN(then.getTime())) return "";

  if (seconds < 60) return `${LAST_SAVED} just now`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return formatTimeAgo(minutes, "minute");
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return formatTimeAgo(hours, "hour");
  const days = Math.floor(hours / 24);
  if (days < 30) return formatTimeAgo(days, "day");
  const months = Math.floor(days / 30);
  if (months < 12) return formatTimeAgo(months, "month");
  const years = Math.floor(months / 12);
  return formatTimeAgo(years, "year");
};

export const createTimeInterval = (
  lastSavedTime: Date,
  setLastSaved: (value: string) => void,
) => {
  const interval = setInterval(() => {
    setLastSaved(timeAgo(lastSavedTime));
  }, 10000); // Update every 10 seconds for demo (change to 60000 for production)

  return (): void => clearInterval(interval);
};

function downloadURI(link: string, name: string): void {
  const anchor = document.createElement("a");
  anchor.style.display = "none";
  anchor.href = link;
  anchor.download = name;
  anchor.target = "_blank";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

export const downloadBinaryFile = (
  binaryFile: BlobPart,
  fileName: string,
): void => {
  const blob = new Blob([binaryFile], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  downloadURI(url, fileName);
  URL.revokeObjectURL(url);
};

export const openBinaryPdfInWindow = (
  viewerWindow: Window | null,
  binaryFile: BlobPart,
  pageNumber?: number,
): boolean => {
  if (!viewerWindow || viewerWindow.closed) {
    return false;
  }

  const blob = new Blob([binaryFile], { type: "application/pdf" });
  const blobUrl = URL.createObjectURL(blob);
  const pageSuffix = pageNumber && pageNumber > 0 ? `#page=${pageNumber}` : "";
  viewerWindow.location.href = `${blobUrl}${pageSuffix}`;

  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);

  return true;
};

export const openBinaryPdfInNewTab = (
  binaryFile: BlobPart,
  pageNumber?: number,
): boolean => {
  const viewerWindow = window.open("", "_blank");
  if (viewerWindow) {
    viewerWindow.opener = null;
  }
  return openBinaryPdfInWindow(viewerWindow, binaryFile, pageNumber);
};

/**
 * Valid coverage name strings returned by the backend coverage-summary endpoint.
 * These are the formatted, human-readable names that the backend returns.
 * This is the single source of truth for valid coverage names.
 */
const VALID_COVERAGE_NAMES = [
  "Dental",
  DENTAL_PPO,
  DENTAL_HMO,
  "Vision",
  "Vision PPO",
  "STD",
  "LTD",
  "Basic Life & AD&D",
  "Voluntary Life & AD&D",
  "Accident",
  CRITICAL_ILLNESS,
  HOSPITAL_INDEMNITY,
  "Cancer",
  "FMLA",
  "EAP",
  INDIVIDUAL_DISABILITY_INSURANCE,
  WHOLE_LIFE,
] as const;

/**
 * Valid coverage name type derived from the backend coverage names.
 * Derived from VALID_COVERAGE_NAMES to maintain single source of truth.
 */
export type CoverageName = (typeof VALID_COVERAGE_NAMES)[number];

/**
 * Runtime type guard to check if a string is a valid CoverageName.
 * Used to validate backend responses before processing.
 *
 * @param value - String value to check
 * @returns True if the value is a valid CoverageName
 *
 * @example
 * ```ts
 * if (isCoverageName(backendValue)) {
 *   const type = labelToCoverageType(backendValue);
 * }
 * ```
 */
export const isCoverageName = (value: string): value is CoverageName =>
  (VALID_COVERAGE_NAMES as readonly string[]).includes(value);

/**
 * Converts a backend coverage name to a CoverageTypeEnum value.
 * This provides type-safe reverse mapping from the backend's formatted names
 * to the enum values used throughout the application.
 *
 * @param label - The backend coverage name (must be a valid CoverageName)
 * @returns The corresponding CoverageTypeEnum value
 * @throws {Error} If an invalid CoverageName is passed (defensive fallback)
 *
 * @example
 * ```ts
 * labelToCoverageType("Dental PPO") // CoverageTypeEnum.DENTAL_PPO
 * labelToCoverageType("STD") // CoverageTypeEnum.STD
 * labelToCoverageType("Basic Life & AD&D") // CoverageTypeEnum.BASIC_LIFE_ADND
 * ```
 */
export const labelToCoverageType = (label: CoverageName): CoverageTypeEnum => {
  const reverseMap: Record<CoverageName, CoverageTypeEnum> = {
    Dental: CoverageTypeEnum.DENTAL,
    [DENTAL_PPO]: CoverageTypeEnum.DENTAL_PPO,
    [DENTAL_HMO]: CoverageTypeEnum.DENTAL_HMO,
    Vision: CoverageTypeEnum.VISION,
    "Vision PPO": CoverageTypeEnum.VISION,
    STD: CoverageTypeEnum.STD,
    LTD: CoverageTypeEnum.LTD,
    "Basic Life & AD&D": CoverageTypeEnum.BASIC_LIFE_ADND,
    "Voluntary Life & AD&D": CoverageTypeEnum.VOLUNTARY_LIFE_ADND,
    Accident: CoverageTypeEnum.ACCIDENT,
    [CRITICAL_ILLNESS]: CoverageTypeEnum.CRITICAL_ILLNESS,
    [HOSPITAL_INDEMNITY]: CoverageTypeEnum.HOSPITAL_INDEMNITY,
    Cancer: CoverageTypeEnum.CANCER,
    FMLA: CoverageTypeEnum.FMLA,
    EAP: CoverageTypeEnum.EAP,
    [INDIVIDUAL_DISABILITY_INSURANCE]:
      CoverageTypeEnum.INDIVIDUAL_DISABILITY_INSURANCE,
    [WHOLE_LIFE]: CoverageTypeEnum.WHOLE_LIFE,
  };

  const result = reverseMap[label];

  // Defensive fallback - should never happen if isCoverageName is used properly
  if (!result) {
    throw new Error(
      `Invalid CoverageName: "${label}" has no mapping to CoverageTypeEnum`,
    );
  }

  return result;
};

/**
 * Converts a coverage type enum to a backend coverage name.
 * This is the inverse of labelToCoverageType and maps to the exact names
 * returned by the backend coverage-summary endpoint.
 *
 * @param type - The coverage type enum value
 * @returns The corresponding backend coverage name
 *
 * @example
 * ```ts
 * coverageTypeToBackendName(CoverageTypeEnum.LTD) // "LTD"
 * coverageTypeToBackendName(CoverageTypeEnum.DENTAL_PPO) // "Dental PPO"
 * ```
 */
export const coverageTypeToBackendName = (
  type: CoverageTypeEnum,
): CoverageName => {
  const nameMap: Record<CoverageTypeEnum, CoverageName> = {
    [CoverageTypeEnum.DENTAL]: "Dental",
    [CoverageTypeEnum.DENTAL_PPO]: "Dental PPO",
    [CoverageTypeEnum.DENTAL_HMO]: "Dental HMO",
    [CoverageTypeEnum.VISION]: "Vision",
    [CoverageTypeEnum.STD]: "STD",
    [CoverageTypeEnum.LTD]: "LTD",
    [CoverageTypeEnum.BASIC_LIFE_ADND]: "Basic Life & AD&D",
    [CoverageTypeEnum.VOLUNTARY_LIFE_ADND]: "Voluntary Life & AD&D",
    [CoverageTypeEnum.ACCIDENT]: "Accident",
    [CoverageTypeEnum.CRITICAL_ILLNESS]: "Critical Illness",
    [CoverageTypeEnum.HOSPITAL_INDEMNITY]: "Hospital Indemnity",
    [CoverageTypeEnum.CANCER]: "Cancer",
    [CoverageTypeEnum.FMLA]: "FMLA",
    [CoverageTypeEnum.EAP]: "EAP",
    [CoverageTypeEnum.INDIVIDUAL_DISABILITY_INSURANCE]:
      "Individual Disability Insurance",
    [CoverageTypeEnum.WHOLE_LIFE]: "Whole Life",
  };
  return nameMap[type];
};
